import { Box, Typography } from '@mui/material';
import {
  EXPANDED_FUNNEL_PREVIEW_HEIGHT,
  MINIMIZED_FUNNEL_PREVIEW_HEIGHT,
} from '../../../constants';
import { DEFAULT_VISIBILITY_MAP } from '../../../constants/visibility';
import { colors } from '@sweep-io/sweep-design';
import { SweepCanvas } from '../../sweep-canvas/SweepCanvas';
import { useState, useEffect } from 'react';
import { getObjectTypeColor } from '../../sweep-canvas/helpers/getObjectTypeColor';
import { useFunnelMapElements } from '../../funnel-map-canvas/useFunnelMapElements';
import noop from 'lodash/noop';
import { SweepCanvasProvider } from '../../sweep-canvas/SweepCanvasProvider';

export interface FunnelMapPreviewProps extends Funnel {
  snapshot?: FunnelSnapshot;
}

export const SingleFunnelMapPreview = ({
  funnel,
  expanded,
  sxProps,
  showPreviewBackground,
  fitViewTimeoutValue,
  renderNoPreview,
  hideControls,
  noFitView,
}: {
  funnel: FunnelMapPreviewProps;
  expanded?: boolean;
  sxProps?: any;
  showPreviewBackground?: boolean;
  fitViewTimeoutValue?: number;
  renderNoPreview?: boolean;
  hideControls?: boolean;
  noFitView?: boolean;
}) => {
  const {
    sweepNodes,
    sweepEdges,
    sweepGroups,
    sweepStandaloneNodes,
    sweepSections,
    sweepStickyNotesNodes,
    sweepTextNodes,
  } = useFunnelMapElements({
    funnelMap: {
      id: 'VIRTUAL_FUNNEL_MAP',
      funnels: {
        [funnel.id]: {
          position: { row: 0, column: 0 },
        },
      },
      recordTypes: {},
      name: funnel.name,
      funnelsData: {
        [funnel.id]: funnel,
      },
      recordTypesData: {},
      hubspotFunnels: {},
      hubspotFunnelsData: {},
      thirdPartyFunnels: {},
      thirdPartyFunnelsData: {},
      createdAt: '',
      createdById: '',
      updatedAt: '',
      updatedById: '',
      isPinned: false,
      metadataElements: {},
      metadataElementsData: {},
      stickyNotes: {},
      version: 0,
    },
    visibilityMap: DEFAULT_VISIBILITY_MAP,
    objectTypesByName: {},
    funnelStatusMap: {},
  });

  const renderNoPreviewWithBackground = (!sweepNodes || sweepNodes.length === 0) && renderNoPreview;
  const [renderCanvas, setRenderCanvas] = useState(true);
  const [localCanvasNodesAndEdges, setLocalCanvasNodesAndEdges] = useState({
    sweepNodes,
    sweepEdges,
    sweepGroups,
    sweepStandaloneNodes,
    sweepSections,
    sweepStickyNotesNodes,
    sweepTextNodes,
  });

  // To fix the animation:
  // Problem is that if one uses the use effect after the nodes change
  // sweepCanvas renders first only after does the fitView. This is perceptible.
  // To achieve perfect rendering and perfect fitView we need to use
  // reactFlow initial render fitView properties. This means that we need a new instance
  // That's why are using a timeout of zero to do that.
  // We also only set the nodes on the next render. It will be unmounted by then.
  useEffect(() => {
    setRenderCanvas(false);
    setLocalCanvasNodesAndEdges({
      sweepNodes,
      sweepEdges,
      sweepGroups,
      sweepStandaloneNodes,
      sweepSections,
      sweepStickyNotesNodes,
      sweepTextNodes,
    });
    const timeoutId = setTimeout(() => setRenderCanvas(true), 100);
    return () => clearTimeout(timeoutId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnel]);

  return (
    <Box
      sx={{
        height: `${expanded ? EXPANDED_FUNNEL_PREVIEW_HEIGHT : MINIMIZED_FUNNEL_PREVIEW_HEIGHT}`,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderBottom: `1px solid ${colors.grey[300]}`,
        position: 'relative',
        backgroundColor: renderNoPreviewWithBackground
          ? getObjectTypeColor(funnel?.funnelDetails?.leadingObject?.objectName).connection
          : '',
        opacity: renderNoPreviewWithBackground ? '.3' : '',
        ...sxProps,
      }}
      data-testid="funnel-preview"
    >
      {renderNoPreviewWithBackground && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            position: 'relative',
            top: '140px',
          }}
        >
          <Typography variant="caption-bold">No preview</Typography>
        </Box>
      )}

      {sweepNodes && sweepNodes.length > 0 && (
        <>
          <div style={{ width: '100%', height: '100%' }}>
            {renderCanvas && (
              <SweepCanvasProvider>
                <SweepCanvas
                  sweepGroups={localCanvasNodesAndEdges.sweepGroups}
                  sweepNodes={localCanvasNodesAndEdges.sweepNodes}
                  sweepEdges={localCanvasNodesAndEdges.sweepEdges}
                  sweepSections={localCanvasNodesAndEdges.sweepSections}
                  sweepStandaloneNodes={localCanvasNodesAndEdges.sweepStandaloneNodes}
                  sweepStickyNotesNodes={localCanvasNodesAndEdges.sweepStickyNotesNodes}
                  sweepTextNodes={localCanvasNodesAndEdges.sweepTextNodes}
                  showPreviewBackground={showPreviewBackground}
                  fitViewTimeoutValue={fitViewTimeoutValue}
                  visibilityMap={DEFAULT_VISIBILITY_MAP}
                  onConnectClick={noop}
                  backgroundObjectType={funnel.funnelDetails.leadingObject.objectName}
                  readonly
                  hideControls={hideControls}
                  noFitView={noFitView}
                  disableMultiSelection
                />
              </SweepCanvasProvider>
            )}
          </div>
        </>
      )}
    </Box>
  );
};
